import { useState, useCallback, useEffect } from "react";
import { validateWebDetailsForm } from "../../../validations/generalForm";
import { useToastPromise } from "../../../components/toastPromise";
import { GeneralController } from "../../../controllers/generalController";
import { Dropdown } from "../../../components/dropdown";
import Button from "@mui/material/Button";
import "../../../assets/css/setttings.css";
import { SiteStatusList } from "../../../constants/siteStatusList";
import { MaintenanceDuration } from "../maintenanceDuration";
import { MaintenanceController } from "../../../controllers/maintenanceController";
import { Textbox } from "../../../components/textbox";
import { CollapsibleContainer } from "../../../components/collapsibleContainer";

export const WebDetails = ({ user }) => {
  const toastPromise = useToastPromise();
  const initialInputs = {
    siteMaintenance: "",
    maintenanceId: "",
    browserTitle: "",
    metaDescription: "",
    metaKeywords: "",
    oGTitle: "",
    oGDescription: "",
    oGURL: "",
    oGType: "",
  };
  const initialMaintenanceInputs = {
    startDateTime: new Date().toLocaleString(),
    endDateTime: new Date().toLocaleString(),
  };
  const [maintenanceInputs, setMaintenanceInputs] = useState(
    initialMaintenanceInputs
  );
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isShowMaintenance, setIsShowMaintenance] = useState(false);
  const [isShowSEO, setIsShowSEO] = useState(true);
  const saveInputs = (name, value) => {
    setInputs((values) => ({ ...values, [name]: value }));
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setMaintenanceInputs(initialMaintenanceInputs);
    setIsShowMaintenance(false);
    setIsShowSEO(true);
    setErrors({});
  };
  const handleTextChange = (event) => {
    saveInputs(event.target.name, event.target.value);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateWebDetailsForm(inputs);
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    if (isShowMaintenance) {
      saveMaintenance().then((maintenanceId) => {
        saveGeneral(maintenanceId);
      });
    } else {
      if (inputs.maintenanceId) deleteMaintenance(inputs.maintenanceId);
      saveGeneral();
    }
  };
  const deleteMaintenance = (maintenanceId) => {
    return new Promise((resolve, reject) => {
      MaintenanceController.delete({
        id: maintenanceId,
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const saveMaintenance = () => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      MaintenanceController.addGetId({
        parameters: {
          ...maintenanceInputs,
          createdBy: user.uid,
          createdDate: new Date().toLocaleString(),
          lastModifiedBy: user.uid,
          lastModifiedDate: new Date().toLocaleString(),
        },
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const saveGeneral = (maintenanceId = "") => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        inputs.id
          ? GeneralController.update({
              parameters: {
                ...inputs,
                id: inputs.id,
                maintenanceId: maintenanceId,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : GeneralController.add({
              parameters: {
                ...inputs,
                maintenanceId: maintenanceId,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        console.debug(data);
        return data;
      },
      errorMessage: (data) => data,
    }).finally(() => {
      setLoading(false);
      resetForm();
      getAll();
    });
  };
  const onDateChange = ({ start, end }) => {
    setMaintenanceInputs({
      startDateTime: start.toLocaleString(),
      endDateTime: end.toLocaleString(),
    });
  };
  const onHandleChange = useCallback((value, name) => {
    setIsShowMaintenance(value === "maintenance");
    setIsShowSEO(value === "active");
    saveInputs(name, value);
  }, []);
  const getAll = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        GeneralController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        const fetchedData = data.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        if (fetchedData.length > 0) {
          setInputs(fetchedData[0]);
          const maintenanceId = fetchedData[0]?.maintenanceId;
          if (maintenanceId) getAllMaintenance(maintenanceId);
          setIsShowSEO(fetchedData[0].siteMaintenance === "active");
        }
        return "Collaboration details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  const getAllMaintenance = (maintenanceId) => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        MaintenanceController.get({
          id: maintenanceId,
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        const maintenanceData = data.data();
        setMaintenanceInputs({
          startDateTime: maintenanceData.startDateTime,
          endDateTime: maintenanceData.endDateTime,
        });
        setIsShowMaintenance(true);
        return "Maintenance details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  useEffect(() => {
    getAll();
  }, []);

  return (
    <section id="webDetails">
      <h4>WEB</h4>
      <hr />
      <Button onClick={handleSubmit} disabled={loading}>
        {loading ? "Saving" : "Save"}
      </Button>
      <form onSubmit={handleSubmit}>
        <div>
          <Dropdown
            name="siteMaintenance"
            title="Site Maintenance"
            value={inputs?.siteMaintenance}
            options={SiteStatusList}
            onChangeFunc={onHandleChange}
            isClearable={false}
            placeholder="Site Maintenance"
            errorMsg={errors.siteMaintenance}
          />
          {isShowMaintenance && (
            <MaintenanceDuration
              onChange={onDateChange}
              initialStartDateValue={
                maintenanceInputs?.startDateTime
                  ? new Date(maintenanceInputs?.startDateTime)
                  : new Date()
              }
              initialEndDateValue={
                maintenanceInputs?.endDateTime
                  ? new Date(maintenanceInputs?.endDateTime)
                  : new Date()
              }
            />
          )}
          {isShowSEO && (
            <CollapsibleContainer
              title="Search Engine Optimization"
              element={
                <div>
                  <Textbox
                    type="text"
                    name="browserTitle"
                    placeholder="Browser Title"
                    value={inputs.browserTitle}
                    onChange={handleTextChange}
                    errorMsg={errors.browserTitle}
                  />
                  <Textbox
                    type="text"
                    name="metaDescription"
                    placeholder="Meta Description"
                    value={inputs.metaDescription}
                    onChange={handleTextChange}
                    errorMsg={errors.metaDescription}
                  />
                  <Textbox
                    type="text"
                    name="metaKeywords"
                    placeholder="Meta Keywords"
                    value={inputs.metaKeywords}
                    onChange={handleTextChange}
                    errorMsg={errors.metaKeywords}
                  />
                  <Textbox
                    type="text"
                    name="oGTitle"
                    placeholder="Open Graph Title"
                    value={inputs.oGTitle}
                    onChange={handleTextChange}
                    errorMsg={errors.oGTitle}
                  />
                  <Textbox
                    type="text"
                    name="oGDescription"
                    placeholder="Open Graph Description"
                    value={inputs.oGDescription}
                    onChange={handleTextChange}
                    errorMsg={errors.oGDescription}
                  />
                  <Textbox
                    type="text"
                    name="oGURL"
                    placeholder="Open Graph URL"
                    value={inputs.oGURL}
                    onChange={handleTextChange}
                    errorMsg={errors.oGURL}
                  />
                  <Textbox
                    type="text"
                    name="oGType"
                    placeholder="Open Graph Type"
                    value={inputs.oGType}
                    onChange={handleTextChange}
                    errorMsg={errors.oGType}
                  />
                </div>
              }
            />
          )}
        </div>
      </form>
    </section>
  );
};
